import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Web3Singleton from 'web3Singleton';

import { IState } from 'models/reducers/i-state.interface';
import { IWalletCurrency } from 'models/wallet-currency-response.interface';

import { PriceCurrencySymbols } from 'enums/price-currency-symbols.enum';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';

import { addErrorMessageAction } from 'actions/global-actions';

import { useBalanceState } from 'hooks/useBalanceState';
import { useEnv } from 'hooks/useEnv';

import { getActiveNetworkOverride } from 'constants/runtime-network';

import { DEFAULT_TOKEN_DECIMALS } from '../constants';
import { delay, RPC_DELAY_MS, withRetry } from '../utils';

import { useProgramData } from './useProgramData';
import { useVipData } from './useVipData';
import { useUserDeposits } from './useUserDeposits';
import { useStakingKpis } from './useStakingKpis';

/**
 * Main hook for fetching and processing staking data.
 * Orchestrates sub-hooks for program data, VIP data, user deposits, and KPIs.
 */
export const useStakingData = () => {
  const { t } = useTranslation('notifications');
  const dispatch = useDispatch();
  const { addNetworkSettings } = useEnv();

  // Redux selectors
  const walletAddress = useSelector((state: IState) => state.wallet.address);
  const { correctChain } = useSelector((state: IState) => state.web3);
  const currencies = useSelector((state: IState) => state.wallet.currencies);

  // Web3 instance
  const lgoWeb3 = Web3Singleton.getInstance();

  // Track if read-only provider is ready
  const [isReadOnlyReady, setIsReadOnlyReady] = useState(false);

  // Initialize read-only provider for accessing data without wallet
  useEffect(() => {
    if (!lgoWeb3.hasReadOnlyProvider() && addNetworkSettings?.rpcUrls?.[0]) {
      lgoWeb3.initializeReadOnlyProvider(addNetworkSettings.rpcUrls[0]);
      setIsReadOnlyReady(true);
    } else if (lgoWeb3.hasReadOnlyProvider()) {
      setIsReadOnlyReady(true);
    }
  }, [lgoWeb3, addNetworkSettings]);

  // Check if we can fetch data (either wallet connected with correct chain, or read-only provider)
  const canFetchData = (correctChain && lgoWeb3.getWeb3()) || isReadOnlyReady;

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-entrancy guards. React 18 strict-mode and chained dep updates can
  // re-fire effects faster than the network can serve them — when that
  // happens against a rate-limited RPC (Infura free tier) we get 429s.
  const inFlightFetchRef = useRef(false);
  const inFlightTokenRef = useRef(false);

  // Currency/Token
  const [currency, setCurrency] = useState<IWalletCurrency | undefined>(undefined);

  // Derived values
  const isWalletConnected = Boolean(walletAddress);
  const tokenDecimals = currency?.decimal ?? DEFAULT_TOKEN_DECIMALS;
  const tokenSymbol = currency?.symbol ?? PriceCurrencySymbols.RMV;
  const hasCurrency = Boolean(currency);

  // Balance from existing hook
  const { balance, refreshBalance } = useBalanceState(currency?.id);

  // Sub-hooks
  const {
    currentPhaseIndex,
    stakingPhases,
    minDeposit,
    fetchProgramData,
    fetchMinDeposit,
    programError,
  } = useProgramData({
    tokenDecimals,
    hasCurrency,
  });

  const { vipPhases, currentVipPhaseIndex, fetchVipData } = useVipData({
    walletAddress: walletAddress || null,
    tokenDecimals,
  });

  const {
    deposits,
    totals,
    fetchDeposits,
    fetchPeriodicalTotalStaked,
    fetchWhitelistedPoolData,
    fetchPeriodicalRewardsAndClaimable,
    resetUserData,
  } = useUserDeposits({
    walletAddress: walletAddress || null,
    tokenDecimals,
    hasCurrency,
  });

  const { kpis, claimableSummary, minDepositDisplay } = useStakingKpis({
    balance,
    totals,
    deposits,
    tokenDecimals,
    minDeposit,
  });

  // Update error state from sub-hooks
  useEffect(() => {
    if (programError) {
      setError(programError);
    }
  }, [programError]);

  /**
   * Fetches and sets the staking token currency.
   * Uses read-only provider if wallet is not connected.
   */
  const fetchStakingToken = useCallback(async () => {
    if (currency || inFlightTokenRef.current) return;

    const override = getActiveNetworkOverride();

    // Without an override we need the backend currencies list to match the
    // staking token. With an override the backend's list is for a different
    // chain, so a backend match won't exist — we synthesize one below.
    if (!override && !currencies?.length) return;

    const preferReadOnly = !!override;
    const stakingService = lgoWeb3.getEffectiveStakingService(preferReadOnly);
    if (!stakingService) return;

    inFlightTokenRef.current = true;
    try {
      const stakingToken = await stakingService.periodicalStakingToken();
      const walletCurrency = currencies?.find(
        (c: IWalletCurrency) => c.address?.toLowerCase() === stakingToken?.toLowerCase()
      );

      if (walletCurrency) {
        setCurrency(walletCurrency);
        return;
      }

      // No backend match. In override mode synthesize a minimal currency so
      // the rest of the staking flow can run with the correct token decimals
      // and address. This currency is dev-impersonation-only and is never
      // sent to any write call.
      if (override) {
        const synthetic = {
          address: stakingToken,
          decimal: DEFAULT_TOKEN_DECIMALS,
          id: -1,
          name: 'RMV (override)',
          network: Number(override.networkId),
          symbol: PriceCurrencySymbols.RMV,
          icon: '',
        } as IWalletCurrency;
        setCurrency(synthetic);
      }
    } catch (e) {
      console.error('Failed to fetch staking token:', e);
      dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
    } finally {
      inFlightTokenRef.current = false;
    }
  }, [lgoWeb3, currencies, currency, dispatch, t]);

  /**
   * Fetches all staking data in batches to avoid rate limiting.
   * Can fetch public program data even without wallet connection using read-only provider.
   */
  const fetchAllData = useCallback(async () => {
    // For public data, we only need currency and either wallet or read-only provider
    if (!currency || !canFetchData) return;
    if (inFlightFetchRef.current) return;

    inFlightFetchRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Batch 1: Program data (public, no wallet needed - uses read-only provider if no wallet)
      await Promise.all([
        withRetry(() => fetchProgramData()),
        withRetry(() => fetchMinDeposit()),
        withRetry(() => fetchVipData()),
      ]);

      // Batch 2: User data
      // Fetch when either the wallet is on the expected chain, or when a
      // read-only RPC is available (dev impersonation / dev network override).
      // The read methods themselves prefer the read-only service when an
      // override is active, so reads still go to the correct RPC.
      const canFetchUserData = walletAddress && (correctChain || isReadOnlyReady);
      if (canFetchUserData) {
        // Delay to avoid rate limiting
        await delay(RPC_DELAY_MS);

        await Promise.all([
          withRetry(() => fetchPeriodicalTotalStaked()),
          withRetry(() => fetchWhitelistedPoolData()),
        ]);

        await delay(RPC_DELAY_MS);

        await withRetry(() => fetchPeriodicalRewardsAndClaimable());

        await delay(RPC_DELAY_MS);

        await withRetry(() => fetchDeposits());
      }

      setIsInitialized(true);
    } catch (e) {
      console.error('Failed to fetch staking data:', e);
      setError('Failed to load staking data');
    } finally {
      setIsLoading(false);
      inFlightFetchRef.current = false;
    }
  }, [
    canFetchData,
    correctChain,
    currency,
    walletAddress,
    isReadOnlyReady,
    fetchProgramData,
    fetchMinDeposit,
    fetchPeriodicalTotalStaked,
    fetchPeriodicalRewardsAndClaimable,
    fetchWhitelistedPoolData,
    fetchDeposits,
    fetchVipData,
  ]);

  const refetch = useCallback(async () => {
    refreshBalance();
    await fetchAllData();
  }, [refreshBalance, fetchAllData]);

  // Initialize currency when we have a provider (wallet or read-only) and currencies are available
  useEffect(() => {
    if (currencies?.length && canFetchData) {
      fetchStakingToken();
    }
  }, [currencies, canFetchData, fetchStakingToken]);

  // Fetch data when currency is available and we can fetch
  useEffect(() => {
    if (currency && canFetchData) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, canFetchData, walletAddress]);

  // Reset user-specific data when wallet disconnects or chain changes
  // Note: VIP data and program data are NOT reset because they can be displayed without wallet
  useEffect(() => {
    if (!walletAddress || !correctChain) {
      resetUserData();
    }
  }, [correctChain, walletAddress, resetUserData]);

  return {
    isLoading,
    isInitialized,
    error,
    isWalletConnected,
    walletAddress: walletAddress || null,
    isCorrectChain: correctChain || false,
    kpis,
    stakingPhases,
    currentPhaseIndex,
    vipPhases,
    currentVipPhaseIndex,
    deposits,
    claimableSummary,
    tokenDecimals,
    tokenSymbol,
    minDeposit,
    minDepositDisplay,
    currency,
    refetch,
  };
};
